/**
 * Tether Stream Protocol v1 message types and validation schemas. See docs/protocol.md.
 * Binary fields are Uint8Array in memory; codecs handle wire encoding.
 */
import { z } from "zod";
export const PROTOCOL_VERSION = 1;
export const MAX_SEGMENT_BYTES = 1024 * 1024;
const bytes = z.custom((v) => v instanceof Uint8Array, "expected bytes");
const id = z.string().min(1).max(64);
const ts = z.number().int().nonnegative();
const seq = z.number().int().nonnegative();
const windowId = z.string().regex(/^\d{8}T\d{4}Z$/);
/** Distinguishes window keys generated by different sessions within the same window. */
const epoch = z.string().regex(/^[a-z0-9]{1,16}$/);
export const Capability = z.enum(["audio", "video", "location", "e2e"]);
export const EncryptionMode = z.enum(["e2e", "plain"]);
export const StreamCodec = z.enum(["opus", "aac", "h264", "h265", "pcm16", "octet"]);
export const Hello = z.object({
    t: z.literal("hello"),
    ts,
    protocol: z.literal(PROTOCOL_VERSION),
    deviceId: id,
    caps: z.array(Capability),
    token: z.string().min(16).max(256),
    /** Highest seq the device knows the gateway acked; gateway replays nothing, device replays from here. */
    resumeSeq: seq.optional(),
    /** Ed25519 signature over the canonical encoding of this message without `sig`. */
    sig: bytes,
});
export const Welcome = z.object({
    t: z.literal("welcome"),
    ts,
    sessionId: id,
    vaultId: id,
    /** Vault public key, 32 bytes. Devices in E2E mode wrap window keys to it. */
    vpk: bytes,
    windowMinutes: z.number().int().positive(),
    serverTs: ts,
    ackedSeq: seq,
});
export const Segment = z.object({
    t: z.literal("segment"),
    ts,
    seq,
    streamId: id,
    windowId,
    epoch,
    codec: StreamCodec,
    startTs: ts,
    durMs: z.number().int().nonnegative(),
    enc: EncryptionMode,
    body: bytes.refine((b) => b.length <= MAX_SEGMENT_BYTES + 64, "segment too large"),
});
export const WindowKey = z.object({
    t: z.literal("windowKey"),
    ts,
    seq,
    windowId,
    epoch,
    /** Encoded sealed blob: R ‖ nonce ‖ ciphertext (see @tether/crypto ecies.encodeSealed). */
    wrapped: bytes,
});
export const LocationPoint = z.object({
    ts,
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    acc: z.number().nonnegative().optional(),
    spd: z.number().nonnegative().optional(),
    hdg: z.number().min(0).max(360).optional(),
    alt: z.number().optional(),
});
export const Location = z.object({
    t: z.literal("location"),
    ts,
    seq,
    enc: EncryptionMode,
    /** Plain points (enc = "plain") ... */
    points: z.array(LocationPoint).optional(),
    /** ... or an encrypted metadata record containing them (enc = "e2e"). */
    body: bytes.optional(),
});
export const EventKind = z.enum([
    "capture_start",
    "capture_stop",
    "sos",
    "trigger",
    "battery",
    "error",
    "heartbeat",
]);
export const Event = z.object({
    t: z.literal("event"),
    ts,
    seq,
    kind: EventKind,
    enc: EncryptionMode,
    data: z.record(z.string(), z.unknown()).optional(),
    body: bytes.optional(),
});
export const Ack = z.object({
    t: z.literal("ack"),
    ts,
    /** Cumulative: everything up to and including seq is durably stored. */
    seq,
});
export const ControlCmd = z.enum(["start", "stop", "config", "ping", "access_notification"]);
export const Control = z.object({
    t: z.literal("control"),
    ts,
    cmd: ControlCmd,
    args: z.record(z.string(), z.unknown()).optional(),
});
export const ErrorMsg = z.object({
    t: z.literal("error"),
    ts,
    code: z.string(),
    message: z.string(),
    fatal: z.boolean(),
});
export const DeviceMessage = z.discriminatedUnion("t", [
    Hello,
    Segment,
    WindowKey,
    Location,
    Event,
    ErrorMsg,
]);
export const GatewayMessage = z.discriminatedUnion("t", [Welcome, Ack, Control, ErrorMsg]);
export const AnyMessage = z.discriminatedUnion("t", [
    Hello,
    Welcome,
    Segment,
    WindowKey,
    Location,
    Event,
    Ack,
    Control,
    ErrorMsg,
]);
export function isSequenced(m) {
    return m.t === "segment" || m.t === "windowKey" || m.t === "location" || m.t === "event";
}
//# sourceMappingURL=messages.js.map